import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import {
  TopologyFilterValues,
  TopologyMaintenanceStatusFilter,
  TopologyNotificationStatusFilter,
} from '../../components/TopologyFilters/TopologyFilters';
import { L3ServiceLocationViewModel, MaintenanceStatus } from '../../model';
import { useL3ServiceLocationsApiGet } from '../L3ServiceLocationsApiGet/L3ServiceLocationsApiGet.hook';
import { useTopologyNetworkServiceEvents } from '../TopologyNetworkServiceEvents/TopologyNetworkServiceEvents.hook';

export interface FilteredL3ServiceLocationsHookResult {
  locations: L3ServiceLocationViewModel[] | null;
  isFilteringInProgress: boolean;
  isFilteringDone: boolean;
  isFetching: boolean;
  isFetched: boolean;
  clearFilters: () => void;
  filters: TopologyFilterValues;
  updateFilters: Dispatch<SetStateAction<TopologyFilterValues>>;
}

const emptyFilters: () => TopologyFilterValues = () => ({
  selectedLocation: null,
  serviceStatus: [],
  maintenanceStatus: [],
  preferenceStatus: [],
  notificationStatus: [],
});

export const useFilteredL3ServiceLocations = (customerId: number | null | undefined): FilteredL3ServiceLocationsHookResult => {
  const { locations: fetchedLocations, isFetching, isFetched } = useL3ServiceLocationsApiGet(customerId);
  const { locations } = useTopologyNetworkServiceEvents(customerId, fetchedLocations, isFetched);
  const [filteredLocations, setFilteredLocations] = useState(locations);
  const [isFilteringInProgress, setFilteringInProgress] = useState(isFetching);
  const [isFilteringDone, setFilteringDone] = useState(isFetched);
  const [filters, setFilters] = useState<TopologyFilterValues>(emptyFilters());

  useEffect(() => {
    setFilteringInProgress(isFetching);
    setFilteringDone(isFetched);
    setFilteredLocations(locations === null ? locations : filterLocations(locations, filters));
  }, [locations, isFetching, isFetched, filters]);

  useEffect(() => {
    setFilters(() => emptyFilters());
  }, [customerId]);

  return {
    locations: filteredLocations,
    isFilteringInProgress,
    isFilteringDone,
    isFetching,
    isFetched,
    filters,
    updateFilters: setFilters,
    clearFilters: () => {
      setFilters(emptyFilters());
    }
  };
};

export const filterLocations = (
  locations: L3ServiceLocationViewModel[],
  filters: TopologyFilterValues,
): L3ServiceLocationViewModel[] => {
  if (filters.selectedLocation) {
    return locations.filter(location => location.orderNumber === filters.selectedLocation?.orderNumber);
  }
  return locations.filter(location => {
    let areConditionsMet = true;
    if (filters.serviceStatus.length) {
      areConditionsMet = areConditionsMet && filters.serviceStatus.includes(location.serviceStatus);
    }
    if (filters.preferenceStatus.length) {
      areConditionsMet = areConditionsMet && filters.preferenceStatus.includes(location.preference)
    }
    if (filters.maintenanceStatus.length) {
      areConditionsMet = areConditionsMet && Object
        .values(filters.maintenanceStatus)
        .map(status => {
          if (status === TopologyMaintenanceStatusFilter.Without) {
            return !location.maintenanceStatuses || !location.maintenanceStatuses.length;
          }
          if (status === TopologyMaintenanceStatusFilter.Ongoing) {
            return Boolean(location.maintenanceStatuses?.includes(MaintenanceStatus.Active));
          }
          if (status === TopologyMaintenanceStatusFilter.Upcoming) {
            return Boolean(location.maintenanceStatuses?.includes(MaintenanceStatus.Planned));
          }
          return false;
        })
        .reduce((prev, next) => Boolean(prev || next));
    }
    if (filters.notificationStatus.length) {
      areConditionsMet = areConditionsMet && Object
        .values(filters.notificationStatus)
        .map(status => {
          if (status === TopologyNotificationStatusFilter.Without) {
            return !location.notificationCount;
          }
          if (status === TopologyNotificationStatusFilter.With) {
            return Boolean(location.notificationCount);
          }
          return false;
        })
        .reduce((prev, next) => Boolean(prev || next));
    }
    return areConditionsMet;
  });
};